import { EntryDetails } from '@/api/entries/types/types';
import { EntryStatus } from '@/components/entry-card';
import { EntryItemPick, EntryItemType } from '@/feature/entries-pickem/components/EntriesList';
import {
    defaultNegativeToZero,
    getEntryDetails,
    getMultiplier,
    getRefundedAmountFromPromo,
} from '@/hooks/use-entries-utils';
import { EventStatus, GameMode, League, PromotionType, Result, Status } from '@/types/api.generated';
import { NFL_FULL_SEASON } from '@/utils/constants';
import { getEntryPayout } from '@/utils/getEntryPayout';
import { nflFullSeason } from '@/utils/league';
import { renderDate } from '@/utils/renderDate';
import { format, parseISO } from 'date-fns';

export type FinishedTab = 'All' | 'Won' | 'Lost' | 'CashedOut' | 'Refunded';

/**
 * An entry can have result=Win AND status=CashedOut.
 * Use this instead of checking result===Win alone.
 */
export const isEntryWin = (entry?: { result?: Result; status?: Status }): boolean =>
    entry?.result === Result.Win && entry?.status !== Status.CashedOut;

export const FINISHED_TAB_FILTERS: { id: FinishedTab; labelKey: string; result?: Result; status?: Status }[] = [
    { id: 'All', labelKey: 'All' },
    { id: 'Won', labelKey: 'Won', result: Result.Win },
    { id: 'Lost', labelKey: 'Lost', result: Result.Loss },
    { id: 'CashedOut', labelKey: 'Cashed out', status: Status.CashedOut },
    { id: 'Refunded', labelKey: 'Refunded', result: Result.Void },
];

const titleModes = {
    DYNAMIC: 'Flex',
    PERFECT: 'Perfect',
    MYSTERY: 'Perfect',
    RACE_TO_PLACE: 'Race to Place', // not used but needed for type safety
};

export const getEntryTitle = (gameMode?: GameMode) => {
    return gameMode ? titleModes[gameMode] : '';
};

export const getEntryScreenStatusBadge = (args: { isRefunded: boolean; status?: Status; result?: Result }) => {
    const { isRefunded, status, result } = args;

    if (status === Status.CashedOut) {
        return EntryStatus.CashedOut;
    }

    if (status === Status.Pending) {
        return EntryStatus.Open;
    }

    if (isRefunded) {
        return EntryStatus.Refunded;
    }

    if (result === Result.Win) {
        return EntryStatus.Won;
    }

    return EntryStatus.Lost;
};

const formatEntryAmount = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

/**
 * @param entry the entry to get the amount for
 * @returns the amount to display based on the entry result and game mode
 */
export const getEntryResultAmount = (entry?: EntryItemType) => {
    const refundedAmount = getRefundedAmountFromPromo(entry);
    if (refundedAmount) {
        const num = Number(refundedAmount);
        return `$${isNaN(num) ? refundedAmount : formatEntryAmount(num)}`;
    }

    if (entry?.result === Result.Void) {
        return `$${formatEntryAmount(defaultNegativeToZero(entry?.amount))}`;
    }

    if (entry?.result === Result.Loss) {
        return '--';
    }

    return `$${formatEntryAmount(defaultNegativeToZero(entry?.toWin))}`;
};

/**
 * @returns the entry multiplier based on settlement status and voided picks
 */
export const getEntryMultiplier = (entry?: EntryItemType) => {
    const entryPayout = getEntryPayout({ picks: entry?.picks });
    const { multiplierType } = getEntryDetails(entry, entryPayout);

    // should display the initial multipliers until settlement is done, else display the multiplierType
    const shouldShowAdjustedMultiplier = shouldDisplayDynamicMultipliersForNonVoided({ entry, entryPayout });
    const multiplierType_ = shouldShowAdjustedMultiplier ? 'adjusted' : multiplierType;
    return getMultiplier(entry, multiplierType_) ?? '';
};

export const isFreeEntry = (entry: EntryItemType | EntryDetails | undefined) =>
    entry?.promotions?.some(promo => promo.type === PromotionType.FreeEntry) ?? false;

export const getCardState = (picks: EntryItemPick[], createdDate: string) => {
    const eventIds = picks.map(pick => pick.eventId);
    const uniqueEventIds = new Set(eventIds);
    const isSameEvent = uniqueEventIds.size === 1;

    const date = new Date(createdDate);
    const dateString = format(date, 'M/d/yyyy');
    const timeString = format(date, 'h:mm a');
    const liveEvents = picks.filter(pick => pick.eventStatus === EventStatus.InProgress).length;

    if (picks.every(pick => pick.result)) {
        return `${dateString}\n at ${timeString}`;
    }

    if (picks.every(pick => nflFullSeason(pick.league))) {
        return NFL_FULL_SEASON;
    }

    const nonSeasonPicks = picks.filter(pick => !nflFullSeason(pick.league));

    if (isSameEvent && liveEvents === 0) {
        return `Start time\n${renderDate(parseISO(nonSeasonPicks[0].eventDate))}`;
    } else {
        if (liveEvents > 0) {
            return liveEvents === 1 ? '1 pick live' : `${liveEvents} picks live`;
        } else {
            const sortedPicks = nonSeasonPicks.sort((a, b) => Date.parse(a.eventDate) - Date.parse(b.eventDate));
            const firstEvent = sortedPicks[0];
            const nextEvent = sortedPicks.find(pick => !pick.result);
            const isEvent = (pick: EntryItemPick) => [League.Pga, League.Ufc].includes(pick.league);
            if (!firstEvent.result) {
                return `${isEvent(firstEvent) ? 'First event' : 'First game'}\n${renderDate(
                    parseISO(firstEvent.eventDate)
                )}`;
            } else if (nextEvent) {
                return `${isEvent(nextEvent) ? 'Next event' : 'Next game'}\n${renderDate(
                    parseISO(nextEvent.eventDate)
                )}`;
            }
        }
    }
};

/* Check for which multipliers to display: dynamic multipliers for non-voided picks or initial dynamic multipliers */
export const shouldDisplayDynamicMultipliersForNonVoided = ({
    entry,
    entryPayout,
}: {
    entry: EntryItemType | EntryDetails;
    entryPayout: { voidedCount: number | undefined; availableCount: number | undefined };
}) => {
    const { voidedCount, initialMultiplierEqualsMultiplier } = getEntryDetails(entry, entryPayout);
    const isRefunded = entry?.result === Result.Void;

    return !!voidedCount && !isRefunded && !initialMultiplierEqualsMultiplier && entry?.status === Status.Settled;
};

export const getEventIdsFromPicks = (
    picks: { eventId: string; player: { id: string } }[] | null | undefined
): string[] => Array.from(new Set(picks?.map(pick => pick.eventId) ?? []));

export const getPlayerIdsFromPicks = (
    picks: { eventId: string; player: { id: string } }[] | null | undefined
): string[] => Array.from(new Set(picks?.map(pick => pick.player.id) ?? []));

export const getEventPlayerIds = (picks: { eventId: string; player: { id: string } }[] | null | undefined) => ({
    eventIds: getEventIdsFromPicks(picks),
    playerIds: getPlayerIdsFromPicks(picks),
});

export const getEntryCancelExpiry = (entry?: {
    canBeCanceled?: boolean;
    canBeCashedout?: boolean;
    cancelTimeFrame?: number;
    createdDate?: string;
}): number | null => {
    if (!entry) {
        return null;
    }
    const { canBeCanceled, canBeCashedout, cancelTimeFrame, createdDate } = entry;
    const hasCancelExpiryData = canBeCanceled && canBeCashedout && createdDate && cancelTimeFrame;
    if (!hasCancelExpiryData) {
        return null;
    }
    return Date.parse(createdDate) + cancelTimeFrame * 1000;
};
